import { Component, Input, OnDestroy, ViewEncapsulation } from '@angular/core';
import { AnnouncementBarSettings } from '../widget-preview.types';

const SAMPLE_ITEM_COUNT = 3;
const SAMPLE_COUNTDOWN_DIGITAL = '01:30:45';
const SAMPLE_COUNTDOWN_COMPACT = '1h 30m';

const EXIT_ANIMATION_MS = 350;
const ENTER_ANIMATION_MS = 500;

const HEIGHT_PADDING: Record<AnnouncementBarSettings['height'], string> = {
  slim: '8px 16px',
  normal: '12px 16px',
  wide: '16px 16px',
};

type MessagePhase = 'idle' | 'exiting' | 'entering';

@Component({
  selector: 'announcement-bar-preview',
  templateUrl: './announcement-bar-preview.component.html',
  styleUrls: ['./announcement-bar-preview.component.scss'],
  // None so the SCSS `<p>` reset reaches the rich-text content injected via [innerHTML].
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AnnouncementBarPreviewComponent implements OnDestroy {
  currentIndex = 0;
  phase: MessagePhase = 'idle';
  isPaused = false;

  private currentSettings: AnnouncementBarSettings | null = null;
  private intervalId: number | null = null;
  private exitTimeoutId: number | null = null;
  private enterTimeoutId: number | null = null;

  // The parent's previewConfig getter recreates settings on every CD cycle, so this setter fires
  // constantly — only restart the interval when timing-relevant inputs actually changed.
  @Input() set settings(value: AnnouncementBarSettings) {
    const oldRotationMs = this.currentSettings ? this.getRotationMs(this.currentSettings) : -1;
    const oldMessageCount = this.currentSettings?.messages?.length ?? 0;

    this.currentSettings = value;

    const newRotationMs = this.getRotationMs(value);
    const newMessageCount = value.messages?.length ?? 0;

    if (this.currentIndex >= newMessageCount) this.currentIndex = 0;

    if (oldRotationMs !== newRotationMs || oldMessageCount !== newMessageCount) {
      this.restartRotation();
    }
  }

  get settings(): AnnouncementBarSettings {
    return this.currentSettings as AnnouncementBarSettings;
  }

  get containerStyle(): Record<string, string> {
    return {
      display: 'flex',
      'justify-content': 'space-between',
      'align-items': 'center',
      padding: HEIGHT_PADDING[this.settings.height],
      'background-color': this.settings.backgroundColor,
      border: this.settings.border.enabled ? `1px solid ${this.settings.border.color}` : 'none',
      'font-size': '14px',
    };
  }

  get phaseClass(): string {
    if (this.phase === 'exiting') return 'announcement-bar__message--exiting';
    if (this.phase === 'entering') return 'announcement-bar__message--entering';
    return '';
  }

  // Colour of whichever message the rotation is showing; empty keeps inheriting, as the storefront does.
  get currentMessageColor(): string {
    return this.settings.messages[this.currentIndex]?.textColor ?? '';
  }

  get renderedMessage(): string {
    return this.renderHtml(this.settings.messages[this.currentIndex]?.html ?? '');
  }

  get renderedMessages(): string[] {
    return (this.settings.messages ?? []).map((m) => this.renderHtml(m?.html ?? ''));
  }

  onMouseEnter(): void {
    this.isPaused = true;
    this.restartRotation();
  }

  onMouseLeave(): void {
    this.isPaused = false;
    this.restartRotation();
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  private restartRotation(): void {
    this.clearTimers();

    if (this.isPaused) return;
    if (!this.currentSettings) return;
    if (this.currentSettings.messages.length <= 1) return;

    const ms = this.getRotationMs(this.currentSettings);
    this.intervalId = window.setInterval(() => this.cycle(), ms);
  }

  private cycle(): void {
    this.phase = 'exiting';
    this.exitTimeoutId = window.setTimeout(() => {
      const count = this.currentSettings?.messages.length ?? 0;
      this.currentIndex = count > 0 ? (this.currentIndex + 1) % count : 0;
      this.phase = 'entering';
      this.enterTimeoutId = window.setTimeout(() => {
        this.phase = 'idle';
      }, ENTER_ANIMATION_MS);
    }, EXIT_ANIMATION_MS);
  }

  private clearTimers(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.exitTimeoutId !== null) {
      clearTimeout(this.exitTimeoutId);
      this.exitTimeoutId = null;
    }
    if (this.enterTimeoutId !== null) {
      clearTimeout(this.enterTimeoutId);
      this.enterTimeoutId = null;
    }
    this.phase = 'idle';
  }

  private getRotationMs(settings: AnnouncementBarSettings): number {
    const { value, unit } = settings.messageRotation;
    return unit === 'minutes' ? value * 60 * 1000 : value * 1000;
  }

  private renderHtml(html: string): string {
    const countdown = this.settings.timer.enabled
      ? this.settings.timer.format === 'digital'
        ? SAMPLE_COUNTDOWN_DIGITAL
        : SAMPLE_COUNTDOWN_COMPACT
      : '';
    return html.replace(/\{item_count\}/g, String(SAMPLE_ITEM_COUNT)).replace(/\{countdown\}/g, countdown);
  }
}
